import type { FormInstance } from '@/services/api/forms';

/**
 * Derive the Category value to show for a form instance in the Form Library
 * list (TRUS-301). A Progress Note stores its category under `pn_category`;
 * fall back to a generic `category` key so other form types that use that key
 * surface too. Returns undefined when neither is present (rendered as "—").
 */
export function getInstanceCategory(instance: Pick<FormInstance, 'field_data'>): string | undefined {
  const fd = (instance?.field_data ?? {}) as Record<string, unknown>;
  const raw = fd.pn_category ?? fd.category;
  if (raw === undefined || raw === null) return undefined;
  const value = String(raw).trim();
  return value === '' ? undefined : value;
}

/**
 * The DERIVED values from a save response, and nothing else.
 *
 * The backend recalculates every `type='computed'` field on save — a PHQ-9
 * total, an AUDIT-C score — so what it stored is not what the editor sent.
 * The editor cannot work these out locally: the formula lives in the
 * organization's own `com_fields` row, by design, so that an organization can
 * change its own. Before this it simply never learned them, and a PHQ-9
 * answered in full went on showing an em dash where its score belonged until
 * the page was reloaded — looking broken at the moment it had just worked.
 *
 * Only `computed_display` fields are picked up. Those hold no typing, so
 * merging them cannot overwrite an answer the user changed while the request
 * was in flight — which merging the whole payload very much could.
 */
export function pickDerivedValues(
  sections: Array<{ fields?: Array<{ field_id?: string; field_type?: string }> }> | undefined | null,
  serverFieldData: Record<string, unknown> | undefined | null,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (!sections || !serverFieldData) return out;
  for (const section of sections) {
    for (const field of section?.fields ?? []) {
      if (field?.field_type !== 'computed_display') continue;
      const id = field.field_id;
      // `in`, not a truthiness check: a legitimately computed 0 — "Not at all"
      // nine times over, the PHQ-9's best possible result — must still come
      // across, and so must a null that clears a stale score.
      if (!id || !(id in serverFieldData)) continue;
      out[id] = serverFieldData[id];
    }
  }
  return out;
}
